import React from 'react';
import { SectionDef, SlotDef, SurfaceDef } from '@/client/types/highlights/slotRegistry';
import { SlotTile } from './SlotTile';

interface Props {
  surface: SurfaceDef;
  onOpenSlot: (slot: SlotDef) => void;
}

/** Lays a single section's slots out in the shape they take on the real page. */
function SectionBlock({ section, onOpenSlot }: { section: SectionDef; onOpenSlot: (s: SlotDef) => void }) {
  const tiles = section.slots.map((slot) => (
    <SlotTile key={slot.key} slot={slot} onOpen={onOpenSlot} className={slot.cellClass} />
  ));

  let grid: React.ReactNode;
  switch (section.layout) {
    case 'hero':
      // A row of wide, short banners.
      grid = <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{tiles}</div>;
      break;
    case 'mosaic':
      // 4 columns × 2 rows — big left, wide center-top, two center-bottom, tall right rail.
      grid = <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:grid-rows-2 sm:auto-rows-[104px]">{tiles}</div>;
      break;
    case 'bottom':
      grid = <div className="grid grid-cols-2 gap-3">{tiles}</div>;
      break;
    case 'menu':
      // Tall featured tile on the left, two stacked on the right.
      grid = <div className="grid max-w-md grid-cols-2 gap-3 sm:grid-rows-2 sm:auto-rows-[92px]">{tiles}</div>;
      break;
  }

  return (
    <section>
      <div className="mb-2.5 flex items-baseline gap-2">
        <h3 className="text-sm font-semibold text-gray-800">{section.label}</h3>
        <span className="text-xs text-gray-400">{section.hint}</span>
      </div>
      {grid}
    </section>
  );
}

/**
 * A mini wireframe of the chosen page. Every promoted spot is drawn roughly
 * where it appears, so an admin recognises "that's the big card up top" without
 * reading a single ID.
 */
export function SurfaceCanvas({ surface, onOpenSlot }: Props) {
  return (
    <div className="space-y-7 rounded-2xl border border-gray-100 bg-gradient-to-b from-gray-50/60 to-white p-5 shadow-sm">
      {surface.sections.map((section) => (
        <SectionBlock key={section.key} section={section} onOpenSlot={onOpenSlot} />
      ))}
    </div>
  );
}
